import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { atom } from 'nanostores';
import { generateId, type JSONValue, type UIMessage } from 'ai';
import { toast } from 'react-toastify';
import { workbenchStore } from '~/lib/stores/workbench';
import { logStore } from '~/lib/stores/logs';
import {
  getMessages,
  getNextId,
  getUrlId,
  setMessages,
  duplicateChat,
  createChatFromMessages,
  getSnapshot,
  type IChatMetadata,
} from './db';
import type { Snapshot } from './types';
import { runtimePromise, runtime } from '~/lib/runtime';
import { capture, captureException } from '~/lib/posthog';
import httpClient from '~/lib/http-client';
import { getStaticPreviewUrl, fetchPreviewUrlWithRetry } from '~/components/workbench/preview-url';
import type { ContextAnnotation } from '~/types/context';
import { $activeProjectId } from '~/lib/stores/projects';
import { chatStore, addOpenTab } from '~/lib/stores/chat';
import { $pendingMessage } from '~/lib/stores/authModal';
import { $wakeUpFn, $resumeFailed } from '~/lib/stores/sandbox';
import { $currentUser } from '~/lib/stores/auth';
import { apiGetClaimStatus } from '~/lib/api/claims';
import { isChatSaveRefused, isSaveForbidden, markChatSaveRefused } from './save-refused';

/**
 * A chat that 404s/403s for an ADMIN may have been handed off via a claim link
 * — the transfer moves it out of their account, so their old tab/URL dies with
 * a generic "not found". Check the claim record and say who has it instead.
 * Returns true when the claimed-away explanation was shown.
 */
async function explainClaimedAway(chatIdStr: string): Promise<boolean> {
  if ($currentUser.get()?.role !== 'admin') {
    return false;
  }

  try {
    const status = await apiGetClaimStatus(chatIdStr);

    if (status.claimedBy) {
      const who = status.claimedBy.displayName || status.claimedBy.email;
      toast.info(`This generation was claimed by ${who} — it now lives in their account.`, { autoClose: 8000 });

      return true;
    }
  } catch {
    // No claim record / not reachable — fall through to the generic message.
  }

  return false;
}

export interface ChatHistoryItem {
  id: string;
  urlId?: string;
  description?: string;
  messages: UIMessage[];
  timestamp: string;
  metadata?: IChatMetadata;
  projectId?: string;
  // Non-null while a generation for this chat is streaming server-side.
  activeStreamId?: string | null;
}

export const chatId = atom<string | undefined>(undefined);
export const description = atom<string | undefined>(undefined);
export const chatMetadata = atom<IChatMetadata | undefined>(undefined);

/*
 * Filler words dropped when condensing a prompt into a short title. Keeps the
 * product/company (e.g. "Kelvin Watches") over the prompt's opening words
 * ("Build a premium landing page for …"). Mirrors the server-side subdomain
 * filter so the title, URL, and repo name all agree.
 */
const TITLE_FILLER_WORDS = new Set([
  'build',
  'create',
  'make',
  'design',
  'develop',
  'generate',
  'the',
  'my',
  'our',
  'for',
  'with',
  'using',
  'use',
  'and',
  'that',
  'this',
  'please',
  'can',
  'you',
  'need',
  'want',
  'just',
  'some',
  'premium',
  'simple',
  'modern',
  'clean',
  'beautiful',
  'responsive',
  'landing',
  'page',
  'website',
  'webpage',
  'site',
  'web',
  'app',
  'application',
  'project',
]);

/** First 1–2 meaningful words of a prompt, Title-Cased ("Kelvin Watches"). '' if none. */
function conciseTitleFromText(text: string): string {
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !TITLE_FILLER_WORDS.has(w));

  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function deriveOptimisticChatDescriptionFromText(value: string): string | undefined {
  const raw = value
    .replace(/\[Model:.*?\]\s*/g, '')
    .replace(/\[Provider:.*?\]\s*/g, '')
    .trim();

  if (!raw) {
    return undefined;
  }

  // Prefer a short product-name title so the persisted description is clean from
  // the first save (never empty — survives an interrupted generation). Falls back
  // to the trimmed prompt when nothing meaningful survives the filter.
  const concise = conciseTitleFromText(raw);

  if (concise) {
    return concise;
  }

  return raw.length > 80 ? `${raw.slice(0, 80)}...` : raw;
}

export function deriveOptimisticChatDescription(messages: UIMessage[]): string | undefined {
  const firstUserMsg = messages.find((m) => m.role === 'user');

  if (!firstUserMsg) {
    return undefined;
  }

  const textContent = (firstUserMsg.parts ?? [])
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map((p) => p.text)
    .join('');

  return deriveOptimisticChatDescriptionFromText(textContent);
}

export async function ensureChatId(): Promise<string> {
  let id = chatId.get();

  if (!id) {
    id = await getNextId();
    chatId.set(id);
  }

  return id;
}

export function useChatHistory(externalId?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const mixedId = externalId;

  const [archivedMessages, setArchivedMessages] = useState<UIMessage[]>([]);
  const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
  const [ready, setReady] = useState<boolean>(false);
  const [urlId, setUrlId] = useState<string | undefined>();
  // Set from the loaded chat — non-null means a generation is streaming and the
  // mounted view should resume it (drives useChat's `resume`).
  const [activeStreamId, setActiveStreamId] = useState<string | null>(null);

  // Track the previous mixedId so we only reset preview/runtime state when
  // actually switching to a different chat, not on every effect re-fire.
  const prevMixedIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // No externalId means Home — clear per-session state but keep tab alive.
    // Guard: only clear if no chat is actively started (the Home route's
    // effect can re-fire during navigation to /chat/:id, which would wipe
    // the chatId mid-transition).
    if (!mixedId && !chatStore.get().started) {
      chatId.set(undefined);
      chatMetadata.set(undefined);
      /*
       * NOTE: description and chatStore are NOT cleared here —
       * the chat tab should persist. Only the X button clears those.
       */
    }

    if (mixedId) {
      const isChatSwitch = prevMixedIdRef.current !== mixedId;
      prevMixedIdRef.current = mixedId;

      // Only reset preview state when switching to a *different* chat.
      // Re-fires of this effect for the same chat must not clear the active
      // preview — doing so mid-stream causes the workbench to flash blank
      // and revert to a stale snapshot.
      if (isChatSwitch) {
        runtime.hasStaticPreview.set(false);
        workbenchStore.previews.set([]);

        /*
         * $wakeUpFn is a global atom holding a closure bound to ONE chat. It is
         * registered per chat below but was never cleared, so it outlived the
         * chat that set it: open chat A while it sleeps, switch to chat B, let B
         * sleep via the session-timeout path (which registers no closure of its
         * own) — and B's Resume button would boot A's sandbox and paint A's
         * preview URL into B. Clear it on every switch; the load below sets the
         * right one for this chat.
         */
        $wakeUpFn.set(null);
        $resumeFailed.set(false);
      }

      /*
       * If this is a brand-new chat navigated from the home-page send flow,
       * React Router state carries `newChat: true`.  Skip the server call
       * that would 404 and go straight to the empty-initialMessages path.
       */
      const isNewChat = !!(location.state as any)?.newChat;

      /*
       * The browser restores `history.state` across a full page reload, so the
       * `newChat` flag (stored by React Router under `history.state.usr`)
       * persists past a refresh. Left in place, reloading a freshly-created chat
       * would re-take the skip-the-fetch path above and render the empty home
       * screen at the chat URL instead of the saved chat. Strip it now that it's
       * consumed — `replaceState` doesn't touch React Router's in-memory
       * location, so `isNewChat` stays valid for this mount while a subsequent
       * reload fetches the chat normally.
       */
      if (isNewChat && typeof window !== 'undefined') {
        const historyState = window.history.state;

        if (historyState?.usr?.newChat) {
          try {
            window.history.replaceState({ ...historyState, usr: { ...historyState.usr, newChat: false } }, '');
          } catch {
            // Clearing the flag is best-effort — a failed replaceState must not
            // abort the chat load below. Worst case a reload re-shows the empty
            // state, i.e. the same pre-fix behavior.
          }
        }
      }

      /*
       * Retry wrapper for transient failures (auth not ready after tab refresh,
       * network hiccup, etc.). Without this, a single failed getMessages() call
       * would redirect to home even for valid chats.
       */
      const fetchWithRetry = async (): Promise<[ChatHistoryItem | undefined, Snapshot | undefined]> => {
        const maxRetries = 3;
        let lastError: Error | undefined;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            console.log(`[ChatHistory] Fetching chat ${mixedId}, attempt ${attempt + 1}/${maxRetries}`);

            const [messages, snap] = await Promise.all([getMessages(mixedId), getSnapshot(mixedId)]);

            // If we got messages with content, return immediately
            if (messages && messages.messages.length > 0) {
              console.log(
                `[ChatHistory] Successfully loaded chat ${mixedId} with ${messages.messages.length} messages`,
              );

              return [messages, snap];
            }

            // Chat exists but has no messages - this is valid, return it
            if (messages) {
              console.log(`[ChatHistory] Chat ${mixedId} exists but has no messages`);
              return [messages, snap];
            }

            // No chat found - could be transient failure, retry
            if (attempt < maxRetries - 1) {
              console.log(`[ChatHistory] Chat ${mixedId} not found, retrying in ${300 * (attempt + 1)}ms`);
              await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
              continue;
            }

            console.log(`[ChatHistory] Chat ${mixedId} not found after ${maxRetries} attempts`);

            return [undefined, snap];
          } catch (err) {
            lastError = err instanceof Error ? err : new Error(String(err));
            console.warn(`[ChatHistory] Fetch attempt ${attempt + 1} failed:`, lastError.message);

            if (attempt < maxRetries - 1) {
              await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
            }
          }
        }

        // All retries failed
        console.error(`[ChatHistory] All ${maxRetries} attempts failed for chat ${mixedId}`);
        throw lastError || new Error('Failed to load chat');
      };

      const fetchData = isNewChat
        ? Promise.resolve([undefined, undefined] as [ChatHistoryItem | undefined, Snapshot | undefined])
        : fetchWithRetry();

      fetchData
        .then(async ([storedMessages, snapshot]) => {
          if (storedMessages && storedMessages.messages.length > 0) {
            const validSnapshot = snapshot || { chatIndex: '', files: {} };
            const summary = validSnapshot.summary;
            const snapshotFileCount = Object.keys(validSnapshot.files || {}).length;

            console.log(
              `[ChatHistory] Loading chat ${mixedId}: ${storedMessages.messages.length} messages, snapshot files: ${snapshotFileCount}, chatIndex: ${validSnapshot.chatIndex}`,
            );

            let startingIdx = -1;
            const snapshotIndex = storedMessages.messages.findIndex((m) => m.id === validSnapshot.chatIndex);

            if (snapshotIndex >= 0) {
              startingIdx = snapshotIndex;
            }

            let filteredMessages = storedMessages.messages.slice(startingIdx + 1);
            let archivedMessages: UIMessage[] = [];

            if (startingIdx >= 0) {
              archivedMessages = storedMessages.messages.slice(0, startingIdx + 1);
            }

            setArchivedMessages(archivedMessages);

            console.log(`[ChatHistory] snapshotIndex=${snapshotIndex}, startingIdx=${startingIdx}`);

            if (startingIdx > 0) {
              console.log(`[ChatHistory] Restoring snapshot — will boot sandbox and write ${snapshotFileCount} files`);

              filteredMessages = [
                {
                  id: generateId(),
                  role: 'user',
                  parts: [{ type: 'text' as const, text: 'Restore project from snapshot' }],
                  annotations: ['no-store', 'hidden'],
                } as UIMessage & { annotations: JSONValue[] },
                {
                  id: storedMessages.messages[snapshotIndex].id,
                  role: 'assistant',
                  parts: [{ type: 'text' as const, text: 'Dunea restored your project from a snapshot.' }],
                  annotations: [
                    'no-store',
                    ...(summary
                      ? [
                          {
                            chatId: storedMessages.messages[snapshotIndex].id,
                            type: 'chatSummary',
                            summary,
                          } satisfies ContextAnnotation,
                        ]
                      : []),
                  ],
                } as UIMessage & { annotations: JSONValue[] },
                ...filteredMessages,
              ];

              // Check if pod is still running before auto-booting
              const snapshotPodStatus = await runtime.checkStatus(mixedId);

              if (snapshotPodStatus === 'running') {
                restoreSnapshot(mixedId, validSnapshot).then(() => {
                  chatStore.setKey('started', true);
                });
              } else {
                console.log('[ChatHistory] Pod is sleeping — showing wake-up banner');
                runtime.status.set('sleeping');

                if (storedMessages.metadata?.hasStaticPreview) {
                  runtime.hasStaticPreview.set(true);
                  workbenchStore.previews.set([{ port: 0, ready: true, baseUrl: getStaticPreviewUrl(mixedId) }]);
                  workbenchStore.autoShowWorkbench({ force: true });
                }

                $wakeUpFn.set(async () => {
                  await restoreSnapshot(mixedId, validSnapshot);
                  chatStore.setKey('started', true);
                });
              }
            } else {
              // Check pod status before booting
              const serverPodStatus = await runtime.checkStatus(mixedId);

              if (serverPodStatus === 'running') {
                // Pod still alive — boot the runtime to connect WebSocket + discover preview
                console.log('[ChatHistory] Pod is running — booting runtime to reconnect');

                const fetchRunningPodPreviewUrl = () =>
                  fetchPreviewUrlWithRetry(
                    async () => (await httpClient.get(`/api/sandbox/${mixedId}/preview-url`)).data?.url,
                    { attempts: 4, initialDelayMs: 5000, maxDelayMs: 20000 },
                  );

                const applyPreviewUrl = (url: string) => {
                  console.log(`[ChatHistory] Preview URL from running pod: ${url}`);

                  let port = 5173;

                  try {
                    const parsed = new URL(url);
                    port = parsed.port ? Number(parsed.port) : 5173;
                  } catch {
                    // URL parse failed, use default port
                  }

                  workbenchStore.previews.set([{ port, ready: true, baseUrl: url }]);
                  workbenchStore.autoShowWorkbench({ force: true });

                  // We have a live preview URL from the pod — it's actually
                  // serving, so declare 'running' (DEV-155). Boot leaves the
                  // runtime at 'starting'; this is the confirmation for the
                  // resume/wake paths, where the pod was already up before we
                  // reconnected so no :5173 port transition fires.
                  runtime.status.set('running');
                };

                runtime
                  .boot(mixedId, storedMessages.metadata?.projectType)
                  .then(async () => {
                    // preview-url may be reviving a dead dev server behind this
                    // call, and it 503s during the server's failure cooldown —
                    // a single-shot fetch here left the board dead forever on
                    // any transient failure. Retry across the cooldown window.
                    const url = await fetchRunningPodPreviewUrl();

                    if (!url) {
                      // Recovery kept failing — show the wake banner so the user
                      // has a visible manual retry instead of a dead board.
                      console.warn('[ChatHistory] Could not fetch preview URL for running pod');
                      runtime.status.set('sleeping');

                      $wakeUpFn.set(async () => {
                        const retryUrl = await fetchRunningPodPreviewUrl();

                        if (!retryUrl) {
                          // Keep the banner up so the button stays reachable.
                          $resumeFailed.set(true);

                          throw new Error('Preview is still unavailable — please try again');
                        }

                        applyPreviewUrl(retryUrl);
                      });

                      return;
                    }

                    applyPreviewUrl(url);
                  })
                  .catch((err: any) => {
                    console.error('[ChatHistory] Failed to reconnect to running pod:', err);
                    runtime.status.set('sleeping');

                    /*
                     * A boot failure reached 'sleeping' without ever setting a
                     * wake closure, so the banner rendered a Resume button whose
                     * click handler early-returns on a null fn — a dead end with
                     * no recovery short of reloading the page. Retry the whole
                     * reconnect, boot included, since boot is what failed here.
                     */
                    $wakeUpFn.set(async () => {
                      await runtime.boot(mixedId, storedMessages.metadata?.projectType);

                      /*
                       * Restore before polling. Boot is what failed on this
                       * path, and if it failed because the pod was reclaimed
                       * then the retry provisions a FRESH one — which comes up
                       * as the blank template. Without this the user is handed
                       * that template as their project with nothing surfaced.
                       */
                      try {
                        await httpClient.post(`/api/sandbox/${mixedId}/restore`);
                      } catch (restoreErr: any) {
                        const status = restoreErr?.response?.status;
                        const code = restoreErr?.response?.data?.code;
                        toast.error(
                          code === 'NO_SNAPSHOT' || status === 404
                            ? "This project's files were never saved to a snapshot, so there is nothing to restore — the preview may show a blank template."
                            : 'Restoring your project files failed — the preview may be incomplete. Try Resume again.',
                        );
                        console.error('[ChatHistory] Restore on reconnect retry failed:', restoreErr?.message);
                      }

                      const retryUrl = await fetchRunningPodPreviewUrl();

                      if (!retryUrl) {
                        $resumeFailed.set(true);

                        throw new Error('Preview is still unavailable — please try again');
                      }

                      applyPreviewUrl(retryUrl);
                    });
                  });

                chatStore.setKey('started', true);
              } else {
                // Pod is sleeping — defer boot until user clicks Wake Up
                console.log('[ChatHistory] Pod is sleeping — showing wake-up banner');
                runtime.status.set('sleeping');

                if (storedMessages.metadata?.hasStaticPreview) {
                  runtime.hasStaticPreview.set(true);
                  workbenchStore.previews.set([{ port: 0, ready: true, baseUrl: getStaticPreviewUrl(mixedId) }]);
                  workbenchStore.autoShowWorkbench({ force: true });
                }

                $wakeUpFn.set(async () => {
                  await runtime.boot(mixedId, storedMessages.metadata?.projectType);

                  try {
                    const res = await httpClient.post(`/api/sandbox/${mixedId}/restore`);
                    console.log(`[ChatHistory] Server restored ${res.data?.restored || 0} files to pod`);
                  } catch (restoreErr: any) {
                    // code === 'NO_SNAPSHOT' is the precise signal that this
                    // chat's files were never pushed to a snapshot repo
                    // (interrupted before its first save) — say so instead of
                    // presenting the blank template as the project. The
                    // status === 404 fallback is a rollout-compat safety net
                    // that is less precise: a plain "sandbox not running" 404
                    // also lands here until the code field is deployed
                    // everywhere.
                    const status = restoreErr?.response?.status;
                    const code = restoreErr?.response?.data?.code;
                    const message =
                      code === 'NO_SNAPSHOT' || status === 404
                        ? "This project's files were never saved to a snapshot, so there is nothing to restore — the preview may show a blank template."
                        : 'Restoring your project files failed — the preview may be incomplete. Try Resume again.';
                    toast.error(message);
                    console.error('[ChatHistory] Restore on wake failed:', restoreErr?.message || restoreErr);
                  }

                  // Poll for preview readiness
                  const maxAttempts = 15;
                  let previewUrl: string | null = null;

                  for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    try {
                      const previewRes = await httpClient.get(`/api/sandbox/${mixedId}/preview-url`);
                      previewUrl = previewRes.data.url;
                      break;
                    } catch {
                      // Not ready yet
                    }

                    await new Promise((r) => setTimeout(r, 1000));
                  }

                  if (!previewUrl) {
                    /*
                     * Resolving here made a failed resume indistinguishable from
                     * a slow one: the banner hid, nothing surfaced, and the board
                     * sat dead until a full page reload. Throw so the caller
                     * keeps the Resume affordance up and the user can retry.
                     *
                     * boot() moved the runtime to 'starting', and the banner only
                     * renders on 'sleeping' — so put it back, or the throw hides
                     * the very button that retries.
                     */
                    console.warn('[ChatHistory] Preview URL not available after polling');
                    $resumeFailed.set(true);

                    throw new Error('The preview did not come up in time — please try Resume again.');
                  }

                  console.log(`[ChatHistory] Preview URL: ${previewUrl}`);

                  let port = 5173;

                  try {
                    const parsed = new URL(previewUrl);
                    port = parsed.port ? Number(parsed.port) : 5173;
                  } catch {
                    // URL parse failed, use default port
                  }

                  workbenchStore.previews.set([{ port, ready: true, baseUrl: previewUrl }]);
                  workbenchStore.autoShowWorkbench({ force: true });

                  chatStore.setKey('started', true);
                });
              }
            }

            setInitialMessages(filteredMessages);

            /*
             * Mark started immediately so header tab state is consistent
             * (don't wait for async sandbox boot)
             */
            chatStore.setKey('started', true);

            setUrlId(storedMessages.urlId);
            description.set(storedMessages.description);
            chatId.set(storedMessages.id);
            chatMetadata.set(storedMessages.metadata);
            setActiveStreamId(storedMessages.activeStreamId ?? null);
          } else if (storedMessages) {
            /*
             * Chat exists but has no messages yet. This can happen if:
             * 1. User created chat, navigated away before sending a message
             * 2. Messages were deleted
             * Proceed with empty messages rather than redirecting.
             */
            console.log(`[ChatHistory] Chat ${mixedId} exists but has no messages`);
            setInitialMessages([]);
            setUrlId(storedMessages.urlId);
            chatId.set(storedMessages.id);
            description.set(storedMessages.description);
            chatMetadata.set(storedMessages.metadata);
            // A brand-new chat mid-first-generation has a row + activeStreamId
            // but no saved messages yet — still resume it.
            setActiveStreamId(storedMessages.activeStreamId ?? null);
          } else if (isNewChat || $pendingMessage.get()) {
            /*
             * New chat: navigated here with newChat state or pending message.
             * Proceed with empty initial messages — ChatImpl will pick up $pendingMessage.
             */
            setInitialMessages([]);
            chatId.set(mixedId);
          } else {
            /*
             * Chat truly doesn't exist on server after retries — a stale URL,
             * or (for an admin) a generation that was claimed away. Explain the
             * handoff when that's what happened; else the generic message.
             */
            console.error(`[ChatHistory] Chat ${mixedId} not found after retries`);
            explainClaimedAway(mixedId).then((claimed) => {
              if (claimed) {
                // Old URL of a handed-off chat → its read-only archive.
                navigate(`/handed-off/${mixedId}`, { replace: true });

                return;
              }

              toast.error('Chat not found or has no messages.');
              navigate('/', { replace: true });
            });

            return;
          }

          setReady(true);
        })
        .catch((error) => {
          console.error(error);
          logStore.logError('Failed to load chat messages or snapshot', error);
          // A 403 here can be an admin's claimed-away chat — open its archive.
          explainClaimedAway(mixedId).then((claimed) => {
            if (claimed) {
              navigate(`/handed-off/${mixedId}`, { replace: true });

              return;
            }

            toast.error("Couldn't open this chat. Please try again.");
            navigate('/', { replace: true });
          });
        });
    } else {
      setReady(true);
    }
  }, [mixedId, navigate]);

  const restoreSnapshot = useCallback(async (id: string, snapshot?: Snapshot) => {
    const fileCount = Object.keys(snapshot?.files || {}).length;
    console.log(
      `[ChatHistory] restoreSnapshot() called — id=${id}, files=${fileCount}, runtime.booted=${runtime.isBooted()}, status=${runtime.status.get()}`,
    );

    // Boot sandbox before restoring files. 'starting' means the socket is
    // already joined (dev server still coming up) — no need to re-boot to write
    // files (DEV-155).
    const bootStatus = runtime.status.get();

    if (!runtime.isBooted() || (bootStatus !== 'running' && bootStatus !== 'starting')) {
      try {
        await runtime.boot(id);
      } catch (err: any) {
        console.error('Failed to boot sandbox for snapshot restore:', err);
        toast.error("Couldn't restore this project. Please try again.");

        return;
      }
    }

    const container = await runtimePromise;

    const validSnapshot = snapshot || { chatIndex: '', files: {} };

    if (!validSnapshot?.files) {
      return;
    }

    Object.entries(validSnapshot.files).forEach(async ([key, value]) => {
      if (key.startsWith(container.workdir)) {
        key = key.replace(container.workdir, '');
      }

      if (value?.type === 'folder') {
        await container.fs.mkdir(key, { recursive: true });
      }
    });
    Object.entries(validSnapshot.files).forEach(async ([key, value]) => {
      if (value?.type === 'file') {
        if (key.startsWith(container.workdir)) {
          key = key.replace(container.workdir, '');
        }

        await container.fs.writeFile(key, value.content);
      }
    });
  }, []);

  return {
    ready: !mixedId || ready,
    initialMessages,
    activeStreamId,
    updateChatMestaData: async (metadata: IChatMetadata) => {
      const id = chatId.get();

      if (!id) {
        return;
      }

      try {
        await setMessages(id, initialMessages, urlId, description.get(), undefined, metadata);
        chatMetadata.set(metadata);
      } catch (error) {
        toast.error("Couldn't save this chat. Please try again.");
        console.error(error);
      }
    },
    storeMessageHistory: async (messages: UIMessage[]) => {
      if (messages.length === 0) {
        return;
      }

      messages = messages.filter((m) => !(m as any).annotations?.includes('no-store'));

      /*
       * Pin chatId synchronously at function entry, BEFORE any await.
       *
       * Previously this function read `chatId.get()` twice — once at the top
       * of the !urlId branch and again at line 533 as `finalChatId` — with an
       * `await getUrlId(idSource)` + `setUrlId(newUrlId)` in between. The
       * setUrlId schedules a React state update; the resulting re-render can
       * re-fire the useChatHistory effect at line 118, which in turn can hit
       * line 124 and call `chatId.set(undefined)` if its guard conditions
       * are momentarily true. Result: the second read returned undefined and
       * the "Failed to save chat messages: Chat ID missing." toast fired
       * even though the chat had a valid id at the moment the save was
       * requested — a classic TOCTOU race. (2026-04-08)
       *
       * Capturing once at entry eliminates the race: if the id was set when
       * the caller asked us to save, it stays pinned for the entire save,
       * regardless of what the nanostore does in the meantime.
       */
      const lockedChatId = chatId.get();

      if (!lockedChatId) {
        /*
         * Genuinely missing id at entry — this should be exceedingly rare
         * because the save effect only runs after streaming, which requires
         * a chatId upstream. Capture rich context so PostHog tells us the
         * true state next time.
         */
        console.error('Cannot save messages, chat ID is not set.');
        captureException(new Error('storeMessageHistory: chatId missing at entry'), {
          context: 'storeMessageHistory.guard',
          messageCount: messages.length,
          urlId,
          pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
        toast.error("Couldn't save your changes. Please try again.");

        return;
      }

      // The server already refused this chat's saves (handed off / not ours);
      // retrying or autosaving it again only produces more 403s.
      if (isChatSaveRefused(lockedChatId)) {
        return;
      }

      if (!urlId) {
        const newUrlId = await getUrlId(lockedChatId);
        setUrlId(newUrlId);

        // Only navigate if the URL actually needs to change.
        // Navigating to the same path creates a new React Router location
        // event which can cascade into effect re-fires and state resets.
        const currentPath = window.location.pathname;

        if (currentPath !== `/chat/${newUrlId}`) {
          navigateChat(newUrlId, navigate);
        }
      }

      let chatSummary: string | undefined = undefined;
      const lastMessage = messages[messages.length - 1];

      if (lastMessage.role === 'assistant') {
        const annotations = (lastMessage as any).annotations as JSONValue[];
        const filteredAnnotations = (annotations?.filter(
          (annotation: JSONValue) =>
            annotation && typeof annotation === 'object' && Object.keys(annotation).includes('type'),
        ) || []) as { type: string; value: any } & { [key: string]: any }[];

        if (filteredAnnotations.find((annotation) => annotation.type === 'chatSummary')) {
          chatSummary = filteredAnnotations.find((annotation) => annotation.type === 'chatSummary')?.summary;
        }
      }

      // Build snapshot using the locked id (no re-read from the store)
      const snapshot: Snapshot | undefined =
        messages.length > 0
          ? {
              chatIndex: messages[messages.length - 1].id,
              files: workbenchStore.files.get(),
              summary: chatSummary,
            }
          : undefined;

      if (!description.get()) {
        const fallback = deriveOptimisticChatDescription(messages);

        if (fallback) {
          description.set(fallback);
        }
      }

      const finalChatId = lockedChatId;

      // On first save, link to the active project (if set from project detail page)
      const activeProject = $activeProjectId.get();

      if (activeProject) {
        $activeProjectId.set(undefined);
      }

      let savedChat: Awaited<ReturnType<typeof setMessages>>;

      try {
        savedChat = await setMessages(
          finalChatId,
          [...archivedMessages, ...messages],
          urlId,
          description.get(),
          undefined,
          chatMetadata.get(),
          activeProject,
          snapshot,
        );
      } catch (err) {
        if (!isSaveForbidden(err)) {
          throw err;
        }

        /*
         * 403: this account may not write the chat (claimed by someone else,
         * or never ours). Resolve instead of throwing so the caller's retry
         * and the streaming autosave stop here, and tell the user once.
         */
        if (markChatSaveRefused(finalChatId)) {
          capture('chat_save_refused', { chatId: finalChatId, messageCount: messages.length });

          void explainClaimedAway(finalChatId).then((claimed) => {
            if (!claimed) {
              toast.info("This chat belongs to another account, so changes here aren't being saved.", {
                autoClose: 8000,
              });
            }
          });
        }

        return;
      }

      if (savedChat.description && savedChat.description !== description.get()) {
        description.set(savedChat.description);
      }
    },
    duplicateCurrentChat: async (listItemId: string) => {
      if (!mixedId && !listItemId) {
        return;
      }

      try {
        const newId = await duplicateChat(mixedId || listItemId);
        navigate(`/chat/${newId}`);
        toast.success('Chat duplicated successfully');
      } catch (error) {
        toast.error("Couldn't duplicate that chat.");
        console.log(error);
      }
    },
    importChat: async (description: string, messages: UIMessage[], metadata?: IChatMetadata) => {
      try {
        const newId = await createChatFromMessages(description, messages, metadata);
        window.location.href = `/chat/${newId}`;
        toast.success('Chat imported successfully');
      } catch (error) {
        console.error(error);
        toast.error("Couldn't import that file. Please check it and try again.");
      }
    },
    exportChat: async (id = urlId) => {
      if (!id) {
        return;
      }

      const chat = await getMessages(id);

      if (!chat) {
        toast.error('Chat not found');
        return;
      }

      const chatData = {
        messages: chat.messages,
        description: chat.description,
        exportDate: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  };
}

/**
 * Navigate to a chat route using React Router.
 * Accepts the navigate function from useNavigate() so React Router stays in sync.
 * Pass `state` to forward React Router location state (e.g. `{ newChat: true }`).
 */
export function navigateChat(
  nextId: string,
  routerNavigate?: (to: string, opts?: any) => void,
  state?: Record<string, unknown>,
) {
  const path = `/chat/${nextId}`;

  if (routerNavigate) {
    routerNavigate(path, { replace: true, state });
  } else {
    // Fallback — should not happen in normal flow
    const url = new URL(window.location.href);
    url.pathname = path;
    window.history.replaceState(state ?? {}, '', url);
  }

  chatStore.setKey('activeChat', path);
  addOpenTab({ chatId: nextId, path, description: description.get() });
}
